import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Calendar, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export default function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { orders } = useCart();

  const order = orders.find(o => o.id === orderId);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Bestelling niet gevonden</h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Terug naar home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Bedankt voor je bestelling!</h1>
          <p className="text-muted-foreground">
            Je bestelling is succesvol geplaatst.
          </p>
        </div>

        {/* Order Info Card */}
        <div className="bg-card border border-primary/10 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-primary/10">
            <div>
              <p className="text-sm text-muted-foreground">Bestelnummer</p>
              <p className="text-lg font-bold text-foreground">{order.id}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Totaal</p>
              <p className="text-lg font-bold text-foreground">{formatPrice(order.total)}</p>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="flex items-start gap-4 mb-6">
            <div className="p-2 rounded-lg bg-primary/10">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Verwachte bezorging</p>
              <p className="text-primary font-semibold">{order.deliveryDate}</p>
            </div>
          </div>

          {/* Order Items */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {order.items.reduce((acc, item) => acc + item.quantity, 0)} artikel(en)
              </span>
            </div>
            {order.items.map(({ product, quantity }) => (
              <div key={product.id} className="flex gap-3 p-3 bg-background rounded-lg">
                <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=No+Image';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                  <p className="text-xs text-muted-foreground">Aantal: {quantity}</p>
                </div>
                <p className="text-sm font-medium text-foreground">
                  {formatPrice(product.price * quantity)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/bestellingen"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-primary/30 text-foreground rounded-lg hover:bg-card transition-colors"
          >
            Bekijk bestellingen
          </Link>
          <Link
            to="/"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Verder winkelen
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
